import {
  useDataLayerOneTrustConfirmationEvent,
  useDataLayerOneTrustDefaultEvent,
} from '@/hooks/useDataLayerEvent';
import { usePortalPrivacyConsentEvent } from '@/hooks/usePortalPrivacyConsentEvent';
import React, { useEffect } from 'react';
import TagManager from 'react-gtm-module';
import { useStorageState } from 'react-storage-hooks';
import {
  DatalayerPopupContentTypes,
  DataLayerTrackEvents,
  IMPRESSION_POPUP_EVENT_NAME,
} from '../App.datalayer.constants';

export const useImpressionPopupEvent = ({
  titleGetter,
  contentType,
  position = '',
  dataLayerEventName = '',
}: {
  titleGetter: () => string;
  contentType: DatalayerPopupContentTypes;
  dataLayerEventName?: string;
  position?: string;
}) => {
  const dataLayerRef = React.useRef({});
  const title = titleGetter();
  const isImpressed = React.useRef({ oneTrust: false, portalPrivacyConsent: false });
  const [isOneTrustInitiated, setIsOneTrustInitiated] = React.useState(false);

  useEffect(() => {
    dataLayerRef.current = {
      event: DataLayerTrackEvents.IMPRESSION_POPUPS,
      event_name: IMPRESSION_POPUP_EVENT_NAME,
      ...(!!title && { title }),
      ...(!!position && { position }),
      content_type: contentType,
    };
  }, [title, contentType, position]);

  const [lastCustomUrl] = useStorageState<string>(sessionStorage, 'SEO.LAST_CUSTOM_URL', '');

  const impressionCallback = React.useCallback(
    ({
      dataLayer = {},
      isDataLayerImpressed = false,
      updateDataLayerImpressedValue = () => {},
    }) => {
      if (!dataLayer) return;
      if (!Object.keys(dataLayer).length) return;
      if (isDataLayerImpressed) return;

      if (!dataLayerEventName || !lastCustomUrl) {
        TagManager.dataLayer({ dataLayer });
        updateDataLayerImpressedValue();
        return;
      }
      if (!!dataLayerEventName && !!lastCustomUrl)
        window.dispatchEvent(
          new CustomEvent(dataLayerEventName, {
            bubbles: true,
            detail: { target: dataLayer },
          }),
        );
      updateDataLayerImpressedValue();
    },
    [dataLayerEventName, lastCustomUrl],
  );

  useDataLayerOneTrustConfirmationEvent(
    () => {
      impressionCallback({
        dataLayer: dataLayerRef.current,
        isDataLayerImpressed: isImpressed.current.oneTrust,
        updateDataLayerImpressedValue: () => {
          isImpressed.current.oneTrust = true;
        },
      });
    },
    { fireOnMount: true },
  );

  useDataLayerOneTrustDefaultEvent(
    (_, unsubscribe = () => {}) => {
      setIsOneTrustInitiated(true);
      unsubscribe();
    },
    { fireOnMount: true },
  );

  usePortalPrivacyConsentEvent({
    enabled: isOneTrustInitiated,
    callback: () => {
      impressionCallback({
        dataLayer: {
          ...(!!dataLayerRef.current && {
            ...dataLayerRef.current,
            event: DataLayerTrackEvents.PORTAL_PAGE_LOAD_IMPRESSION_POPUPS,
          }),
        },
        isDataLayerImpressed: isImpressed.current.portalPrivacyConsent,
        updateDataLayerImpressedValue: () => {
          isImpressed.current.portalPrivacyConsent = true;
        },
      });
    },
  });
};
